import { Button, Dropdown, InputField, LabelComponent, Stack, DateAndTime } from 'components';
import { useFormik, FormikProps } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { FC, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  MedicalVisitsItem,
  postMedicalVisits,
  getMedicalVisits
} from 'store/medicalVisits/medicalVisitsActions';

import style from './medicalVisits.module.scss';
import UnsavedChangesModal from '../unsavedChangesModal/unsavedChangesModal';

type FormikInputProps = {
  formik: FormikProps<any>;
  name: string;
};

const FormikInput: FC<FormikInputProps> = ({ formik, name }) => {
  return (
    <Stack width="100%">
      <LabelComponent text={name.charAt(0).toUpperCase() + name.slice(1)} />
      <InputField
        classnamesProps={style.inputField}
        id={name}
        name={name}
        type="text"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values[name] || ''}
        placeholder={`Enter ${name}`}
      />
    </Stack>
  );
};

interface Props {
  handleCancel: () => void;
  setFormDirty: (dirty: boolean) => void;
  unsavedChangesModalOpen: boolean;
  setUnsavedChangesModalOpen: (open: boolean) => void;
  handleModalClose: () => void;
}

const MedicalVisitForm: FC<Props> = ({
  handleCancel,
  setFormDirty,
  unsavedChangesModalOpen,
  setUnsavedChangesModalOpen,
  handleModalClose
}) => {
  const { clientId } = useParams();
  const dispatch = useAppDispatch();
  const { medicalVisitsTypes, medicalVisitsStatuses, medicalVisitsList } = useAppSelector(
    (state) => state.medicalVisits
  );

  const { page, size, sorts, filters } = medicalVisitsList;

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      typeId: '',
      statusId: '',
      location: null,
      provider: null,
      reason: null,
      dateOfEncounter: null,
      timeOfEncounter: null,
      time: null
    },
    validationSchema: Yup.lazy(() => {
      return Yup.object().shape({
        dateOfEncounter: Yup.string().nullable(),
        timeOfEncounter: Yup.string().nullable(),
        time: Yup.string().nullable(),
        typeId: Yup.number().nullable(),
        location: Yup.string().nullable(),
        provider: Yup.string().nullable(),
        reason: Yup.string().nullable(),
        statusId: Yup.number().nullable()
      });
    }),
    onSubmit: (values) => {
      if (clientId) {
        const data: MedicalVisitsItem = {
          ...values,
          typeId: values.typeId ? parseInt(values.typeId, 10) : undefined,
          statusId: values.statusId ? parseInt(values.statusId, 10) : undefined,
          timeOfEncounter: values.time ? dayjs(values.time, 'HH:mm:ss').format('hh:mm A') : null
        };
        dispatch(postMedicalVisits({ clientId, data })).then(() => {
          formik.resetForm();
          handleCancel();
          dispatch(getMedicalVisits({ clientId, page, size, sorts, filters }));
        });
      }
    }
  });

  useEffect(() => {
    setFormDirty(formik.dirty);
  }, [formik.dirty]);

  return (
    <form onSubmit={formik.handleSubmit} data-test-id="medical-visit-form">
      <Stack
        width="100%"
        data-test-id="medical-visit-form-top"
        display="flex"
        flexDirection="row"
        gap="16px"
        mb="12px"
      >
        <Stack width="100%">
          <DateAndTime
            dateValue={formik.values.dateOfEncounter}
            datePickerStyles={style.inputField}
            onChangeDate={(date: string) => {
              formik.setFieldValue('dateOfEncounter', date).then(() => {
                formik.setFieldTouched('dateOfEncounter', true);
              });
            }}
            onBlurDate={() => formik.setFieldTouched('dateOfEncounter', true)}
            timeValue={formik.values.time}
            timePickerStyles={style.inputField}
            onChangeTime={(time: string) => {
              if (time === 'Invalid Date') {
                formik.setFieldValue('time', null).then(() => {
                  formik.setFieldTouched('time', true);
                });
              } else {
                formik.setFieldValue('time', time).then(() => {
                  formik.setFieldTouched('time', true);
                });
              }
            }}
            onBlurTime={() => formik.setFieldTouched('time')}
            hasLabel
          />
        </Stack>
        <Stack width="100%">
          <LabelComponent text="Type" />
          <Dropdown
            className={style.dropdown}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            data={medicalVisitsTypes}
            label="Select"
            name="typeId"
            id="typeId"
            value={formik.values.typeId}
            fitWidth
          />
        </Stack>
        <FormikInput formik={formik} name="location" />
      </Stack>
      <Stack width="100%" display="flex" flexDirection="row" gap="16px" mb="12px">
        <FormikInput formik={formik} name="provider" />
        <FormikInput formik={formik} name="reason" />
        <Stack width="100%">
          <LabelComponent text="Status" />
          <Dropdown
            className={style.dropdown}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            data={medicalVisitsStatuses}
            label="Select"
            name="statusId"
            id="statusId"
            value={formik.values.statusId}
            fitWidth
          />
        </Stack>
      </Stack>
      <Stack justifyContent="flex-end" mb="36px" gap="8px" flexDirection="row">
        <Button
          text="Cancel"
          theme="transparent"
          type="button"
          handleOnClick={handleCancel}
          customStyleClass={style.confirmButton}
        />
        <Button
          disabled={!formik.isValid || !formik.dirty || formik.isSubmitting}
          type="submit"
          text="Confirm"
          theme="primary"
          customStyleClass={style.confirmButton}
        />
      </Stack>
      <UnsavedChangesModal
        formik={formik}
        open={unsavedChangesModalOpen}
        setOpen={setUnsavedChangesModalOpen}
        onDiscard={handleModalClose}
      />
    </form>
  );
};

export default MedicalVisitForm;
